import { Product } from '../types';
import { PRODUCTS_URL } from '../config/api-config';
import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';

interface APIError {
  message: string;
  code?: string;
  details?: any;
  status?: number;
}

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export const useProductsApi = () => {

    const { getAuthToken } = useAuth();
    const getHeaders = createApiHeadersGetter(getAuthToken);
  
    const getProducts = async (): Promise<Product[]> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(
          `${PRODUCTS_URL}`,
          {
            method: 'GET',
            headers
          }
        );
        if (!response.ok) {
          throw new Error('Failed to fetch Products');
        }
        return response.json();
      } catch (error) {
        console.error('Error fetching Products:', error);
        throw error;
      }
    };

    const createProduct = async (productName: string): Promise<Product> => {
        try {
          const headers = await getHeaders();
          const response = await fetch(
            `${PRODUCTS_URL}`,
            {
              method: 'POST',
              headers,
              body: JSON.stringify({ productName })
            }
          );
          if (!response.ok) {
            throw new Error('Failed to create Product');
          }
          return response.json();
        } catch (error) {
          console.error('Error creating Product:', error);
          throw error;
        }
      };
  
      // This function will either find an existing product or create a new one
      const findOrCreateProduct = async (productName: string): Promise<Product> => {
        try {
          // First, get all products to check if it exists
          const products = await getProducts();
          const existingProduct = products.find(
            product => product.productName.toLowerCase() === productName.toLowerCase()
          );
          
          if (existingProduct) {
            return existingProduct;
          }
          
          // If not found, create a new product
          return await createProduct(productName);
        } catch (error) {
          console.error('Error in findOrCreateProduct:', error);
          throw error;
        }
      };

    return {
        getProducts,
        createProduct,
        findOrCreateProduct
    }
}